// frontend/modules/customer-category/hooks/useFieldConfiguration.ts
import { useCallback } from 'react';
import { useCustomerCategoryStore } from '../stores/customer-category-store';
import { message } from 'antd';
import {
    getFieldConfigurationForCategoryGrouped,
    getGroupedFieldConfiguration
} from '@/shared/api/client/field-configuration-controller';
import {CustomerCategoryFieldDTO, GroupedFieldConfigurationDTO} from "@/shared/api/model";
import { EntityFieldsResponse, FieldMetadata } from '../types';

export const useFieldConfiguration = () => {
    const {
        groupedFieldConfiguration,
        categoryFields,
        isLoading: storeLoading,
        error: storeError,

        setGroupedFieldConfiguration,
        setCategoryFields,
        setIsLoading,
        setError
    } = useCustomerCategoryStore();

    // Fetch grouped field configuration for a customer type
    const fetchGroupedFieldConfiguration = useCallback(async (customerType: 'INDIVIDUAL' | 'GROUP' | 'BUSINESS') => {
        try {
            setIsLoading(true);
            const response = await getGroupedFieldConfiguration(customerType);
            
            if (response) {
                setGroupedFieldConfiguration(response);
            }
            setError(null);
        } catch (error: any) {
            console.error('Error fetching grouped field configuration:', error);
            setError(error.message || 'Failed to fetch field configuration');
            message.error('Failed to fetch field definitions');
        } finally {
            setIsLoading(false);
        }
    }, [setGroupedFieldConfiguration, setIsLoading, setError]);

    // Alias for consistency
    const fetchEntityFieldsByType = fetchGroupedFieldConfiguration;
    const fetchEntityFields = () => fetchGroupedFieldConfiguration('INDIVIDUAL');

    const entityFields: EntityFieldsResponse | null = groupedFieldConfiguration
        ? groupedFieldConfiguration.fieldGroups?.reduce((acc, group) => {
            const targetKey = group.groupKey === 'custInfo' || group.groupKey === 'CustInfo'
                ? 'custInfoFields'
                : 'customerFields';

            (group.fields || []).forEach(field => {
                const fieldName = field.fieldName;
                if (!fieldName) {
                    return;
                }

                acc[targetKey][fieldName] = {
                    label: field.label || fieldName,
                    type: field.type || 'text',
                    defaultRequired: field.defaultRequired ?? false
                } as FieldMetadata;
            });

            return acc;
        }, {
            customerFields: {} as Record<string, FieldMetadata>,
            custInfoFields: {} as Record<string, FieldMetadata>
        }) || {
            customerFields: {},
            custInfoFields: {}
        }
        : null;

    // Fetch field configuration for a specific category
    const fetchCategoryFields = useCallback(async (categoryId: number) => {
        try {
            setIsLoading(true);
            const response = await getFieldConfigurationForCategoryGrouped(categoryId);
            
            if (response) {
                setGroupedFieldConfiguration(response);

                const allFields: CustomerCategoryFieldDTO[] = [];

                if (response.fieldGroups) {
                    response.fieldGroups.forEach(group => {
                        if (group.fields) {
                            const groupFields = group.fields.map(field => ({
                                fieldName: field.fieldName || '',
                                entityType: group.groupKey === 'custInfo' || group.groupKey === 'CustInfo' ? 'CustInfo' : 'Customer',
                                customLabel: field.label || '',
                                isVisible: field.isVisible ?? true,
                                isMandatory: field.defaultRequired ?? false,
                                displayOrder: field.displayOrder || 0,
                                ...field
                            }));
                            allFields.push(...groupFields);
                        }
                    });
                }

                setCategoryFields(allFields);
                setError(null);
            }
        } catch (error: any) {
            console.error('Error fetching category fields:', error);
            setError(error.message || 'Failed to fetch category fields');
            message.error('Failed to fetch field configuration');
        } finally {
            setIsLoading(false);
        }
    }, [setCategoryFields, setGroupedFieldConfiguration, setIsLoading, setError]);

    // Use store loading and error states
    const isLoading = storeLoading;
    const error = storeError;

    return {
        groupedFieldConfiguration,
        entityFields,
        categoryFields,
        isLoading,
        error,

        fetchGroupedFieldConfiguration,
        fetchEntityFields,
        fetchEntityFieldsByType,
        fetchCategoryFields
    };
};